import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

/**
 * Inicializa o Sentry para monitoramento de erros e performance.
 *
 * Calibração (plano Free do Sentry — ~5k erros/mês, ~10k tx/mês):
 * - tracesSampleRate baixo em prod, alto em dev
 * - replay aleatório mínimo + replay 100% quando há erro
 * - ignoreErrors/denyUrls cortam ruído de extensões e terceiros (GTM, Hotjar, Stripe.js)
 * - tracePropagationTargets liga frontend ↔ edge functions ↔ Supabase
 *
 * Erros server-side das edge functions sobem para o MESMO projeto Sentry
 * via `supabase/functions/_shared/sentry.ts` (environment = "edge").
 */
export const initSentry = () => {
  // Não inicializa em CI para não atrasar o startup
  if (import.meta.env.CI === 'true') {
    return;
  }

  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

  if (!sentryDsn) {
    console.info('ℹ️ Sentry não configurado (defina VITE_SENTRY_DSN para habilitar)');
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    release: import.meta.env.VITE_SENTRY_RELEASE || 'development',
    environment: import.meta.env.MODE || 'development',

    enableLogs: true,

    // 🔒 LGPD: nada de PII para serviço externo
    sendDefaultPii: false,

    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Propaga trace headers para Supabase e domínios do projeto.
    // Permite ver uma chamada do frontend até a edge function no mesmo trace.
    tracePropagationTargets: [
      /^\/(?!\/)/, // requests same-origin
      /^https:\/\/.*\.supabase\.co\/.*/,
      'auli.live',
      'auli.lovable.app',
      'lovableproject.com',
    ],

    // Sample rates conservadores (Free tier)
    tracesSampleRate: import.meta.env.PROD ? 0.05 : 1.0,
    replaysSessionSampleRate: import.meta.env.PROD ? 0.02 : 0,
    replaysOnErrorSampleRate: 1.0,

    // Ruído conhecido — não vira issue
    ignoreErrors: [
      // Browser/extensões
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      // Fetch abortado intencionalmente (navegação, troca de query)
      'AbortError',
      'The user aborted a request.',
      // GTM / analytics de terceiros
      "Can't find variable: gtag",
      'gtag is not defined',
      // Stripe.js carregamento intermitente
      'Failed to load Stripe.js',
    ],
    denyUrls: [
      /chrome-extension:\/\//,
      /moz-extension:\/\//,
      /safari-extension:\/\//,
      /googletagmanager\.com/,
      /static\.hotjar\.com/,
      /js\.stripe\.com\/v3/,
    ],

    beforeSend(event, hint) {
      if (event.user) {
        delete event.user.ip_address;
      }
      if (event.request) {
        delete event.request.headers;
      }

      const error = hint.originalException;
      if (error && typeof error === 'object' && 'message' in error) {
        const message = String((error as { message?: unknown }).message ?? '');
        if (message.includes('chrome-extension://')) return null;
        if (message.includes('moz-extension://')) return null;
        if (message.includes('Failed to fetch') && event.tags?.handled === 'true') {
          return null;
        }
      }
      return event;
    },

    beforeSendTransaction(event) {
      // Transações muito curtas só pagam quota
      if (event.start_timestamp && event.timestamp) {
        const duration = (event.timestamp - event.start_timestamp) * 1000;
        if (duration < 100) return null;
      }
      return event;
    },
  });

  // Tag global de versão (facilita filtrar regressões por deploy)
  const release = import.meta.env.VITE_SENTRY_RELEASE;
  if (release) {
    Sentry.setTag('app.version', String(release));
  }
};

/**
 * Configura o contexto do usuário no Sentry.
 * Deve ser chamado após o login/carregamento do perfil.
 */
export const setSentryUser = (
  userId: string | null,
  profile?: {
    name?: string | null;
    email?: string | null;
    activeMode?: string | null;
    role?: string | null;
  }
) => {
  if (!userId) {
    Sentry.setUser(null);
    Sentry.setTag('user_profile', undefined as unknown as string);
    Sentry.setTag('role', undefined as unknown as string);
    Sentry.setTag('mode', undefined as unknown as string);
    return;
  }

  Sentry.setUser({
    id: userId,
    username: profile?.name || undefined,
    email: profile?.email || undefined,
  });

  if (profile?.activeMode) {
    Sentry.setTag('user_profile', profile.activeMode); // legado, mantido por compat
    Sentry.setTag('mode', profile.activeMode);
  }
  if (profile?.role) {
    Sentry.setTag('role', profile.role);
  }
};

/**
 * Captura um erro manualmente (para try/catch onde queremos reportar)
 */
export const captureError = (
  error: unknown,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  }
) => {
  const err = error instanceof Error ? error : new Error(String(error));
  Sentry.captureException(err, {
    tags: context?.tags,
    extra: context?.extra,
  });
};

/**
 * Adiciona um breadcrumb customizado (vira contexto da próxima exception)
 */
export const addBreadcrumb = (
  message: string,
  data?: Record<string, unknown>,
  level: 'info' | 'warning' | 'error' = 'info'
) => {
  Sentry.addBreadcrumb({
    message,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
};

/**
 * Inicia um span de performance customizado.
 */
export const startSpan = <T>(
  context: { op: string; name: string; attributes?: Record<string, string | number | boolean> },
  callback: (span: ReturnType<typeof Sentry.startInactiveSpan>) => T
): T => {
  return Sentry.startSpan(
    {
      op: context.op,
      name: context.name,
      attributes: context.attributes,
    },
    callback
  );
};

/**
 * Logger leve para hooks que ainda importam de `@/lib/sentry`.
 * IMPORTANTE: info/debug NÃO viram issue (vira breadcrumb).
 * Só `error` captura exceção. Para logs estruturados em código novo,
 * prefira `@/lib/logger`.
 */
export const logger = {
  trace: (message: string, extra?: Record<string, unknown>) =>
    addBreadcrumb(message, extra, 'info'),
  debug: (message: string, extra?: Record<string, unknown>) =>
    addBreadcrumb(message, extra, 'info'),
  info: (message: string, extra?: Record<string, unknown>) =>
    addBreadcrumb(message, extra, 'info'),
  warn: (message: string, extra?: Record<string, unknown>) =>
    addBreadcrumb(message, extra, 'warning'),
  error: (message: string, extra?: Record<string, unknown>) => {
    Sentry.captureMessage(message, { level: 'error', extra });
  },
  fatal: (message: string, extra?: Record<string, unknown>) => {
    Sentry.captureMessage(message, { level: 'fatal', extra });
  },
};

export const SentryErrorBoundary = Sentry.ErrorBoundary;
export const withSentryRouteTracing = Sentry.withSentryRouting;
